import { ref } from 'vue';
import axios, { type AxiosInstance, type AxiosError } from 'axios';
import { getCurrentInstance } from 'vue';
import type {
  ContractSigningError,
  ErrorResponseData,
  HttpStatusCode,
} from '@/types/contractSigning';
import {
  parseErrorResponse,
  createContractSigningError,
  getErrorConfigFromStatus,
} from '@/utils/digitalAgreementErrorHandling';

/**
 * Agreement details returned from the API
 */
export interface AgreementDetails {
  uniqueKey: string;
  communityName?: string;
  pdfBlob?: Blob;
}

/**
 * Parsed agreement headers
 */
interface AgreementHeaders {
  uniqueKey: string;
  communityName: string | null;
}

/**
 * Get or create axios instance (memoized)
 */
let apiInstance: AxiosInstance | null = null;

function getApiInstance(): AxiosInstance {
  if (apiInstance) {
    return apiInstance;
  }

  // Get backend base URL from config provider
  const instance = getCurrentInstance();
  const appConfig = instance?.appContext.config.globalProperties.$appconfig;
  const backendBaseUrl = appConfig?.BACKEND_BASEURL;

  // Create axios instance
  apiInstance = axios.create({
    baseURL: backendBaseUrl,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Add response interceptor
  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(error)
  );

  return apiInstance;
}

/**
 * Parse agreement headers from response
 * @param headers - Response headers
 * @param fallbackUniqueKey - Fallback unique key if not in headers
 * @returns Parsed headers
 */
function parseAgreementHeaders(
  headers: Record<string, unknown>,
  fallbackUniqueKey: string
): AgreementHeaders {
  // Use exact header names from server configuration
  // Axios may normalize headers to lowercase
  const uniqueKeyFromHeader =
    (headers['X-Agreement-Unique-Key'] as string) ||
    (headers['x-agreement-unique-key'] as string) ||
    fallbackUniqueKey;

  const communityName =
    (headers['X-Community-Name'] as string) ||
    (headers['x-community-name'] as string) ||
    null;

  return {
    uniqueKey: uniqueKeyFromHeader,
    communityName,
  };
}

/**
 * Handle error response from API
 * @param status - HTTP status code
 * @param data - Response data (Blob or ErrorResponseData)
 * @returns Contract signing error
 */
async function handleErrorResponse(
  status: number,
  data: Blob | ErrorResponseData
): Promise<ContractSigningError> {
  let responseData: ErrorResponseData;

  if (data instanceof Blob) {
    responseData = await parseErrorResponse(data);
  } else {
    responseData = data;
  }

  const errorConfig = getErrorConfigFromStatus(status as HttpStatusCode);
  const errorMsg = responseData?.error || errorConfig.i18nKey;

  return createContractSigningError(
    new Error(errorMsg),
    status as HttpStatusCode,
    responseData
  );
}

/**
 * Composable for contract signing API calls
 */
export function useContractSigning() {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const api = getApiInstance();

  async function fetchAgreementWithPdf(uniqueKey: string): Promise<AgreementDetails> {
    loading.value = true;
    error.value = null;

    const url = `/contractSigning/${uniqueKey}/pdf`;

    try {
      const response = await api.get(url, {
        responseType: 'blob'
      });

      // Handle error status codes (404, 409, 410)
      if (response.status !== 200) {
        const contractError = await handleErrorResponse(
          response.status,
          response.data as Blob
        );
        error.value = contractError.message;
        throw contractError;
      }

      const pdfBlob = response.data as Blob;
      const headers = parseAgreementHeaders(response.headers || {}, uniqueKey);

      return {
        uniqueKey: headers.uniqueKey,
        communityName: headers.communityName || undefined,
        pdfBlob
      };
    } catch (err) {
      const axiosError = err as AxiosError<Blob | ErrorResponseData>;

      // Handle axios errors with response
      if (axiosError.response) {
        const contractError = await handleErrorResponse(
          axiosError.response.status,
          axiosError.response.data
        );
        // Include headers in the error response so they can be accessed
        if (contractError.response) {
          contractError.response.headers = axiosError.response.headers;
        }
        error.value = contractError.message;
        throw contractError;
      }

      const errorMsg = axiosError.message || 'Failed to fetch agreement';
      error.value = errorMsg;
      throw createContractSigningError(err, undefined, undefined);
    } finally {
      loading.value = false;
    }
  }

  async function acceptAgreement(uniqueKey: string): Promise<{ success: boolean; message: string }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post(`/contractSigning/${uniqueKey}/accept`);
      return response.data as { success: boolean; message: string };
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponseData>;
      
      // Handle 405 (preconditions not met) as success case
      // The agreement is accepted, but application completion failed due to preconditions, but sepa was set
      if (axiosError.response?.status === 405) {
        return {
          success: true,
          message: 'Agreement accepted successfully'
        };
      }

      const errorMsg =
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Failed to accept agreement';
      error.value = errorMsg;

      if (axiosError.response) {
        throw await handleErrorResponse(
          axiosError.response.status,
          axiosError.response.data
        );
      }

      throw createContractSigningError(err, undefined, undefined);
    } finally {
      loading.value = false;
    }
  }

  async function declineAgreement(uniqueKey: string, reason: string): Promise<{ success: boolean; message: string }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post(`/contractSigning/${uniqueKey}/decline`, {
        reason
      });
      return response.data as { success: boolean; message: string };
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponseData>;
      const errorMsg =
        axiosError.response?.data?.error ||
        axiosError.message ||
        'Failed to decline agreement';
      error.value = errorMsg;

      if (axiosError.response) {
        throw await handleErrorResponse(
          axiosError.response.status,
          axiosError.response.data
        );
      }

      throw createContractSigningError(err, undefined, undefined);
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    error,
    fetchAgreementWithPdf,
    acceptAgreement,
    declineAgreement
  };
}

